import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const EmployeeSheets = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Mock data - replace with API calls
  const mockEmployee = {
    id: employeeId,
    name: 'John Smith',
    role: 'Sales Rep',
    email: 'john@company.com',
    status: 'active'
  };

  const mockSheets = [
    {
      id: 'custom',
      name: 'Custom Lead Sheet',
      lastModified: '2024-01-15',
      rows: 25,
      type: 'personal'
    },
    {
      id: 'leads-q1',
      name: 'Q1 Leads',
      lastModified: '2024-01-14',
      rows: 45,
      type: 'personal'
    },
    {
      id: 'follow-ups',
      name: 'Follow-ups',
      lastModified: '2024-01-13',
      rows: 12,
      type: 'personal'
    },
    {
      id: 'shared-1',
      name: 'Shared Lead Pool',
      lastModified: '2024-01-15',
      rows: 67,
      type: 'assigned'
    },
    {
      id: 'team-1',
      name: 'Team Leads',
      lastModified: '2024-01-14',
      rows: 23,
      type: 'assigned'
    },
  ];

  useEffect(() => {
    setIsLoading(true);
    // Simulate API calls
    setTimeout(() => {
      setEmployee(mockEmployee);
      setSheets(mockSheets);
      setIsLoading(false);
    }, 500);
  }, [employeeId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleCreateSheet = () => {
    const newSheet = {
      id: `sheet-${Date.now()}`,
      name: 'New Sheet',
      lastModified: new Date().toISOString().split('T')[0],
      rows: 0,
      type: 'personal'
    };
    setSheets([newSheet, ...sheets]);
    navigate(`/sheets/${newSheet.id}`);
  };

  const handleAssignSheet = () => {
    // TODO: Implement sheet assignment functionality
    console.log('Assign sheet to employee', employeeId);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading employee sheets...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            ← Back to Drive
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-800">{employee.name}</h1>
            <p className="text-sm text-gray-600">{employee.role} • {employee.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleAssignSheet}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            Assign Sheet
          </button>
          <button
            onClick={handleCreateSheet}
            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
          >
            + Create Sheet
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white p-3 rounded shadow">
          <div className="text-lg font-bold text-blue-600">{sheets.length}</div>
          <div className="text-xs text-gray-600">Total Sheets</div>
        </div>
        <div className="bg-white p-3 rounded shadow">
          <div className="text-lg font-bold text-green-600">
            {sheets.filter(s => s.type === 'personal').length}
          </div>
          <div className="text-xs text-gray-600">Personal</div>
        </div>
        <div className="bg-white p-3 rounded shadow">
          <div className="text-lg font-bold text-purple-600">
            {sheets.filter(s => s.type === 'assigned').length}
          </div>
          <div className="text-xs text-gray-600">Assigned</div>
        </div>
        <div className="bg-white p-3 rounded shadow">
          <div className="text-lg font-bold text-orange-600">
            {sheets.reduce((sum, s) => sum + s.rows, 0)}
          </div>
          <div className="text-xs text-gray-600">Total Rows</div>
        </div>
      </div>

      {/* Sheets Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {sheets.map((sheet) => (
          <div
            key={sheet.id}
            className="bg-white rounded shadow hover:shadow-md transition-shadow cursor-pointer p-3"
            onClick={() => navigate(`/sheets/${sheet.id}`)}
          >
            <div className="text-center">
              <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center mx-auto mb-2">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-800 truncate mb-1">{sheet.name}</h3>
              <div className="flex items-center justify-center gap-1 mb-1">
                <span className={`w-2 h-2 rounded-full ${sheet.type === 'personal' ? 'bg-blue-400' : 'bg-purple-400'
                  }`}></span>
                <span className="text-xs text-gray-500">{sheet.rows}</span>
              </div>
              <div className="text-xs text-gray-400">{formatDate(sheet.lastModified)}</div>
            </div>
          </div>
        ))}
      </div>

      {sheets.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">No sheets found for this employee</div>
          <div className="text-gray-400 text-sm">Create a new sheet or assign an existing one</div>
        </div>
      )}
    </div>
  );
};

export default EmployeeSheets;


